import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, ChevronLeft, ChevronRight, ChevronDown, Clock, User, FileText, Receipt, BookOpen, CreditCard, Settings, Shield, Download, Upload, Copy, Globe } from 'lucide-react';

const MODULE_ICONS = {
  invoices: FileText,
  bills: Receipt,
  journals: BookOpen,
  bank_feeds: CreditCard,
  settings: Settings,
  payments: CreditCard,
  contacts: User,
  other_receipts: Download,
  other_payments: Upload,
  other: Shield,
};

const MODULE_COLORS = {
  invoices: 'bg-blue-100 text-blue-700',
  bills: 'bg-red-100 text-red-700',
  journals: 'bg-purple-100 text-purple-700',
  bank_feeds: 'bg-emerald-100 text-emerald-700',
  settings: 'bg-gray-100 text-gray-700',
  payments: 'bg-amber-100 text-amber-700',
  contacts: 'bg-cyan-100 text-cyan-700',
  other_receipts: 'bg-teal-100 text-teal-700',
  other_payments: 'bg-orange-100 text-orange-700',
  other: 'bg-slate-100 text-slate-700',
};

const ACTION_COLORS = {
  created: 'bg-green-100 text-green-700',
  updated: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
  approved: 'bg-emerald-100 text-emerald-700',
  issued: 'bg-indigo-100 text-indigo-700',
  voided: 'bg-orange-100 text-orange-700',
  payment_recorded: 'bg-teal-100 text-teal-700',
  imported: 'bg-cyan-100 text-cyan-700',
  exported: 'bg-purple-100 text-purple-700',
};

const ActivityLogTab = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);

  // Filters
  const [filterModule, setFilterModule] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Iter 888 - track which rows are expanded to show full context
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const toggleRow = (idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const limit = 30;
  const totalPages = Math.ceil(total / limit);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (filterModule !== 'all') params.set('module', filterModule);
      if (filterAction !== 'all') params.set('action', filterAction);
      if (filterUser !== 'all') params.set('user_id', filterUser);
      if (searchQuery) params.set('search', searchQuery);

      const { data } = await axios.get(`${API}/audit-log/?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [token, page, filterModule, filterAction, filterUser, searchQuery]);

  const fetchMeta = useCallback(async () => {
    try {
      const [modRes, usrRes] = await Promise.all([
        axios.get(`${API}/audit-log/modules`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/audit-log/users`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setModules(modRes.data.modules || []);
      setUsers(usrRes.data.users || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterModule, filterAction, filterUser, searchQuery]);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + 
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4" data-testid="activity-log-tab">
      <CardHeader className="px-0 pt-0">
        <CardTitle className="text-lg">Activity Log</CardTitle>
        <CardDescription>
          Track all actions performed by team members across your business
        </CardDescription>
      </CardHeader>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search activity..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="activity-search-input"
          />
        </div>
        <Select value={filterModule} onValueChange={setFilterModule}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="activity-module-filter">
            <SelectValue placeholder="All Modules" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            {modules.map(m => (
              <SelectItem key={m} value={m}>{m.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-full sm:w-[140px]" data-testid="activity-action-filter">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="created">Created</SelectItem>
            <SelectItem value="updated">Updated</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="voided">Voided</SelectItem>
            <SelectItem value="payment_recorded">Payment</SelectItem>
            <SelectItem value="imported">Imported</SelectItem>
            <SelectItem value="exported">Exported</SelectItem>
          </SelectContent>
        </Select>
        {users.length > 1 && (
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="activity-user-filter">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Logs List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Clock className="w-5 h-5 animate-spin mr-2" />
              Loading activity...
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="activity-log-empty">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No activity recorded yet</p>
              <p className="text-sm mt-1">Actions performed across your business will appear here</p>
            </div>
          ) : (
            <div className="divide-y" data-testid="activity-log-list">
              {logs.map((log, idx) => {
                const ModIcon = MODULE_ICONS[log.module] || Shield;
                const moduleColor = MODULE_COLORS[log.module] || MODULE_COLORS.other;
                const actionColor = ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600';
                const details = log.details || {};
                const hasDetails = Boolean(details && Object.values(details).some(v => v !== null && v !== undefined && v !== ''));
                const isExpanded = expandedRows.has(idx);
                return (
                  <div key={idx} data-testid={`activity-log-row-${idx}`}>
                    <button
                      type="button"
                      onClick={() => hasDetails && toggleRow(idx)}
                      className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left ${hasDetails ? 'cursor-pointer' : 'cursor-default'}`}
                      data-testid={`activity-log-toggle-${idx}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${moduleColor}`}>
                        <ModIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium break-words" data-testid={`activity-log-description-${idx}`}>
                          {log.description}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${actionColor}`}>
                            {(log.action || '').replace(/_/g, ' ')}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {(log.module || 'other').replace(/_/g, ' ')}
                          </Badge>
                          {details.method && (
                            <span className="text-[10px] font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                              {details.method}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">{log.user_email}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">
                          {formatDate(log.created_at)}
                        </span>
                        {hasDetails && (
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </div>
                    </button>
                    {isExpanded && hasDetails && (
                      <div className="px-4 pb-3 pl-15" data-testid={`activity-log-details-${idx}`}>
                        <div className="ml-11 rounded-md border bg-muted/30 px-3 py-2.5 space-y-1.5 text-xs">
                          {details.path && (
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-muted-foreground min-w-[80px]">Endpoint:</span>
                              <code className="font-mono text-[11px] break-all">{details.method} {details.path}</code>
                            </div>
                          )}
                          {details.status_code != null && (
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-muted-foreground min-w-[80px]">Status:</span>
                              <code className={`font-mono text-[11px] px-1.5 py-0.5 rounded ${details.status_code < 300 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {details.status_code}
                              </code>
                            </div>
                          )}
                          {details.entity_id && (
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-muted-foreground min-w-[80px]">Record:</span>
                              <code className="font-mono text-[11px] break-all">{details.entity_id}</code>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(details.entity_id); }}
                                className="text-muted-foreground hover:text-foreground"
                                title="Copy id"
                                data-testid={`copy-entity-${idx}`}
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          {details.ip_address && (
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-muted-foreground min-w-[80px]">IP:</span>
                              <span className="font-mono text-[11px] flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {details.ip_address}
                              </span>
                            </div>
                          )}
                          {details.user_agent && (
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-muted-foreground min-w-[80px]">Device:</span>
                              <span className="text-[11px] text-muted-foreground break-all">{details.user_agent}</span>
                            </div>
                          )}
                          {/* Render any extra detail keys that aren't part of the standard envelope */}
                          {Object.entries(details).filter(([k]) => !['method','path','status_code','entity_id','ip_address','user_agent'].includes(k)).map(([k, v]) => (
                            <div key={k} className="flex items-start gap-2">
                              <span className="font-medium text-muted-foreground min-w-[80px]">{k.replace(/_/g, ' ')}:</span>
                              <span className="text-[11px] break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </div>
                          ))}
                          <div className="flex items-start gap-2 pt-1 mt-1 border-t border-border/50">
                            <span className="font-medium text-muted-foreground min-w-[80px]">Logged at:</span>
                            <span className="text-[11px]">{log.created_at}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-sm text-muted-foreground">
                {total} total entries
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="activity-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="activity-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogTab;
